import Image from 'components/Common/Image/Image'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import Group from 'components/utils/Group'
import Clickable from 'components/Common/Clickable'
import BodyText from 'LuxKit/Typography/BodyText'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Heading from 'LuxKit/Typography/Heading'
import { rem } from 'polished'
import type { MouseEventHandler } from 'react'
import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import type { ChatMapMarker } from 'tripPlanner/components/ChatMap/types'
import TripDrawer, { type TripDrawerAnimation } from 'tripPlanner/components/GenericDrawer/TripDrawer'
import TripDrawerHeaderTitle from 'tripPlanner/components/GenericDrawer/TripDrawerHeaderTitle'
import AddToTripAction from 'tripPlanner/components/GenericDrawer/AddToTripAction'
import type { TripItineraryData, MapCameraPosition } from 'tripPlanner/api/types'
import { mapPlaceImageIds } from 'tripPlanner/utils/placeImages'
import { useGetPlaceInfo } from 'tripPlanner/hooks/api/chat'
import { useStreamPlaceHighlights } from 'tripPlanner/hooks/api/useStreamPlaceHighlights'
import MarkdownRender from 'LuxKit/MarkdownRender'
import TextButton from 'LuxKit/Button/TextButton'
import LineImagesIcon from 'LuxKit/Icons/line/LineImagesIcon'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import LineGlobeIcon from 'LuxKit/Icons/line/LineGlobeIcon'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import LineExternalLinkAltIcon from 'LuxKit/Icons/line/LineExternalLinkAltIcon'
import Divider from 'LuxKit/Divider'
import OpeningHours from 'tripPlanner/components/TripModal/ExploreItems/OpeningHours'
import ExploreReviews from 'tripPlanner/components/TripModal/ExploreItems/ExploreReviews'
import type { ExploreItemReview } from 'tripPlanner/types/common'
import GooglePlaceRatingDisplay from 'tripPlanner/components/GooglePlaceRating/GooglePlaceRatingDisplay'
import GooglePlacePriceLevel, { shouldShowPriceLevel } from 'tripPlanner/components/GooglePlacePriceLevel'
import DrawerDetailMobileImageCarousel from './DrawerDetailMobileImageCarousel'
import {
  ContentWrapper,
  GridContainer,
  GalleryButtons,
  GridImageClickable,
  PRIMARY_GALLERY_IMAGE_CLASS,
  SECONDARY_GALLERY_IMAGE_CLASS,
} from './TripPlanDetailDrawer.styles'
import {
  DrawerDetailBodyShell,
  DrawerDetailHeroLoading,
  splitDrawerDetailHero,
} from './drawerDetailHeroLayout'
import { useChatStreamStatus } from 'tripPlanner/assistant/contexts/ChatStreamStatusContext'
import { useTripPlannerEventBusOptional } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import { usePlaceInfoGalleryLightbox } from '../hooks/usePlaceInfoGalleryLightbox'
import {
  shouldRenderTripDrawerTitleInContent,
  useRightPanelTripDrawerPresentation,
} from 'tripPlanner/components/GenericDrawer/TripDrawerPresentationContext'
import TripDrawerAssistantBridge, { TripDrawerAssistantFooter } from './TripDrawerAssistantBridge'
import { getDrawerGalleryImageKey, useDrawerGalleryImages } from '../hooks/useDrawerGalleryImages'
import useGalleryButtonKind from '../hooks/useGalleryButtonKind'
import useRebrandVersion from 'hooks/useRebrandVersion'
import DrawerDetailMap, { DrawerDetailMapFrame } from './DrawerDetailMap'

const VenueBookingCard = styled(Clickable)`
  padding: ${rem(16)};
  border: 1px solid ${(props) => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${(props) => props.theme.borderRadius.L};
  inline-size: 100%;
`

interface Props {
  isOpen: boolean
  onClose: () => void
  onAfterClose?: () => void
  onBack?: () => void
  animation?: TripDrawerAnimation
  googlePlaceId: string
  placeName: string
  itemId?: number
  onDelete?: (itemId: number) => void
  itineraryData?: TripItineraryData
  onSendMessage?: (message: string) => void
  hideAddToTrip?: boolean
}

function PlaceInfoDrawer({
  isOpen,
  onClose,
  onAfterClose,
  onBack,
  animation,
  googlePlaceId,
  placeName,
  itemId,
  onDelete,
  itineraryData,
  onSendMessage,
  hideAddToTrip = false,
}: Props) {
  const intl = useIntl()
  const galleryButtonKind = useGalleryButtonKind()
  /*
   * The rating renders a size smaller than the category label beside it. Both
   * sizes draw a 16px star, so only the label changes. Gated so the legacy
   * palette keeps its current typography and the experiment stays isolated.
   */
  const ratingSize = useRebrandVersion() === 'rebrand' ? 'M' : 'S'
  const eventBus = useTripPlannerEventBusOptional()
  const { isStreaming } = useChatStreamStatus()
  const drawerPresentation = useRightPanelTripDrawerPresentation()
  const showTitleInContent = shouldRenderTripDrawerTitleInContent(drawerPresentation)

  const {
    data: placeInfo,
    isLoading,
    isError,
    refetch,
  } = useGetPlaceInfo(isOpen ? googlePlaceId : undefined, 'preview')

  const {
    markdown: highlightsMarkdown,
    isLoading: isHighlightsLoading,
    isError: isHighlightsStreamError,
    retry: handleRetryHighlights,
  } = useStreamPlaceHighlights(googlePlaceId, isOpen)

  const noReviewAvailableText = intl.formatMessage({
    id: 'G_mEsWQ1kyaGcBeZaLxW',
    defaultMessage: 'No review available',
    description: 'Fallback display text when a place review has no content',
  })

  const mapMarkers = useMemo((): Array<ChatMapMarker> => {
    if (!placeInfo) {
      return []
    }

    return [
      {
        markerKey: googlePlaceId,
        lat: placeInfo.lat,
        lng: placeInfo.lng,
        name: placeName,
        label: placeName,
        variant: 'primary',
      },
    ]
  }, [placeInfo, googlePlaceId, placeName])

  const allGalleryImages = useMemo(() => {
    return mapPlaceImageIds(placeInfo?.imageIds)
  }, [placeInfo?.imageIds])

  const {
    availableImages: availableGalleryImages,
    failedImageKeys: failedImageIds,
    gridImages,
    imageCount: gridImageCount,
    handleImageError: handleGalleryImageError,
  } = useDrawerGalleryImages({
    images: allGalleryImages,
    resetKey: googlePlaceId,
  })

  const { openGalleryLightboxAtIndex } = usePlaceInfoGalleryLightbox({
    googlePlaceId,
    isOpen,
    images: availableGalleryImages,
    failedImageIds,
  })

  const mappedReviews = useMemo((): Array<ExploreItemReview> | undefined => {
    if (!placeInfo?.reviews) return undefined
    return placeInfo.reviews.map(
      (review): ExploreItemReview => ({
        authorName: review.authorDisplayName,
        authorUrl: '',
        language: '',
        authorPhotoUrl: '',
        relativeTimeDescription: review.relativePublishTimeDescription,
        text: review.text ?? noReviewAvailableText,
        rating: review.rating,
        publishTime: review.publishTime,
      }),
    )
  }, [placeInfo?.reviews, noReviewAvailableText])

  const handleGalleryImageClick = useCallback<MouseEventHandler<HTMLElement>>(
    (event) => {
      const imageIndex = Number(event.currentTarget.dataset.index) || 0
      openGalleryLightboxAtIndex(imageIndex)
    },
    [openGalleryLightboxAtIndex],
  )

  const openGalleryLightbox = useCallback(() => {
    openGalleryLightboxAtIndex(0)
  }, [openGalleryLightboxAtIndex])

  const handleRetry = useCallback(() => {
    refetch()
  }, [refetch])

  const handleDelete = useCallback(() => {
    if (itemId !== undefined && onDelete) {
      onDelete(itemId)
      onClose()
    }
  }, [itemId, onDelete, onClose])

  const showLoadedContent = !isLoading && placeInfo
  const showMobileFullBleedGallery = showTitleInContent

  const placePhoneNumber =
    placeInfo &&
    'internationalPhoneNumber' in placeInfo &&
    typeof placeInfo.internationalPhoneNumber === 'string'
      ? placeInfo.internationalPhoneNumber
      : undefined
  const websiteDisplayLabel = placeInfo?.website?.split('?')[0]

  const showDeleteOption =
    itemId !== undefined && onDelete !== undefined && !isStreaming

  const placeCoordinates = useMemo(() => {
    if (placeInfo?.lat == null || placeInfo?.lng == null) return undefined
    return { lat: placeInfo.lat, lng: placeInfo.lng }
  }, [placeInfo?.lat, placeInfo?.lng])

  const handleEnlargeMap = useMemo(() => {
    if (!eventBus || !placeCoordinates) return undefined
    return (camera: MapCameraPosition) => {
      onClose()
      eventBus.dispatch({
        type: 'show_place_on_map',
        camera,
        data: {
          googlePlaceId,
          name: placeName,
          coordinates: placeCoordinates,
        },
      })
    }
  }, [eventBus, onClose, placeCoordinates, googlePlaceId, placeName])
  const showVenueBookingCard = !!(
    showLoadedContent &&
    placeInfo.openingHours &&
    placeInfo.website
  )

  const addToTripAction =
    !hideAddToTrip && itineraryData && onSendMessage ? (
      // No onAfterAction: the drawer deliberately stays open after adding so the traveller
      // sees the confirmation and the trigger flipping to the assigned day. The day picker
      // still closes itself.
      <AddToTripAction
        itineraryData={itineraryData}
        onSendMessage={onSendMessage}
        placeName={placeName}
        placeId={googlePlaceId}
        coordinates={placeCoordinates}
      />
    ) : undefined

  const failedToLoadPlaceInfoText = intl.formatMessage({
    id: 'x-FwtyfMQ_-9NzK8SHxV',
    defaultMessage: 'Failed to load place information.',
    description:
      'Error text when place information fails to load in the place info drawer',
  })

  const retryLabel = intl.formatMessage({
    id: 'aFCT8x_umCSnLcGYvgnA',
    defaultMessage: 'Retry',
    description:
      'Button label to retry loading content in the place info drawer',
  })

  const viewGalleryLabel = intl.formatMessage({
    id: '8KmBGuIpCvz119d9lHNW',
    defaultMessage: 'View gallery',
    description:
      'Button label to open the image gallery lightbox in the place info drawer',
  })

  const gallerySection = useMemo(() => {
    if (isLoading) {
      return <DrawerDetailHeroLoading mobileFullWidth={showMobileFullBleedGallery} />
    }

    if (!showLoadedContent) {
      return null
    }

    if (showMobileFullBleedGallery) {
      return availableGalleryImages.length > 0 && (
        <DrawerDetailMobileImageCarousel
          images={availableGalleryImages}
          onOpenLightbox={openGalleryLightboxAtIndex}
        />
      )
    }

    if (gridImages.length === 0) {
      return null
    }

    return (
      <GridContainer
        $imageCount={gridImageCount}
        $matchOfferPageAspect
      >
        {gridImages.map(({ image, lightboxIndex }, gridImageIndex) => (
          <GridImageClickable
            className={gridImageIndex === 0 ? PRIMARY_GALLERY_IMAGE_CLASS : SECONDARY_GALLERY_IMAGE_CLASS}
            key={image.id ?? image.url}
            onClick={handleGalleryImageClick}
            data-index={lightboxIndex}
          >
            <Image
              image={image}
              fit="center"
              data-image-key={getDrawerGalleryImageKey(image)}
              onError={handleGalleryImageError}
            />
          </GridImageClickable>
        ))}
        {availableGalleryImages.length > 1 && (
          <GalleryButtons
            direction="horizontal"
            gap={8}
            horizontalAlign="end"
          >
            <TextButton
              kind={galleryButtonKind}
              variant="dark"
              startIcon={<LineImagesIcon />}
              onClick={openGalleryLightbox}
            >
              {viewGalleryLabel}
            </TextButton>
          </GalleryButtons>
        )}
      </GridContainer>
    )
  }, [
    availableGalleryImages,
    galleryButtonKind,
    gridImageCount,
    gridImages,
    handleGalleryImageClick,
    handleGalleryImageError,
    isLoading,
    openGalleryLightbox,
    openGalleryLightboxAtIndex,
    showLoadedContent,
    showMobileFullBleedGallery,
    viewGalleryLabel,
  ])

  const locationHeading = intl.formatMessage({
    id: 'pLh1qsAGPGiZwm7xSmWK',
    defaultMessage: 'Location',
    description:
      'Heading for the location map section in the place info drawer',
  })

  const venueBookingTitle = intl.formatMessage({
    id: 'PIOB5b5MxD5-ZsNaj2Bs',
    defaultMessage: 'Visit their site for details',
    description:
      'Title for the venue booking card shown in the place info drawer when opening hours exist',
  })

  const venueBookingSubtitle = intl.formatMessage({
    id: 'zolIuHlUz8Ak3KIGVy_0',
    defaultMessage: 'Opens external site for details & availability',
    description:
      'Subtitle for the venue booking card shown in the place info drawer when opening hours exist',
  })

  const venueBookingLinkLabel = intl.formatMessage({
    id: 'ksVt_IXPqfEoAcDUZPFn',
    defaultMessage: 'Link',
    description:
      'Button label for the venue booking external link in the place info drawer',
  })

  const failedToLoadHighlightsText = intl.formatMessage({
    id: '66uhTXEs-xkAd7-BzB5r',
    defaultMessage: 'Failed to load highlights.',
    description:
      'Error text when place highlights fail to load in the place info drawer',
  })

  const showPriceLevel = shouldShowPriceLevel(placeInfo?.priceLevel)

  // On the right-panel (sticky-header) layout the rating, category and price level move
  // up beside the title so they read as one line; on mobile they stay in content.
  const headerSubtitle =
    placeInfo && (placeInfo.rating || placeInfo.category || showPriceLevel) ? (
      <Group direction="horizontal" gap={8} verticalAlign="center">
        {placeInfo.rating && (
          <GooglePlaceRatingDisplay
            rating={placeInfo.rating}
            ratingsCount={placeInfo.userRatingCount}
            size="S"
          />
        )}
        {placeInfo.rating && placeInfo.category && (
          <Divider kind="primary" direction="vertical" />
        )}
        {placeInfo.category && (
          <BodyText variant="small" colour="light-foreground-alt">
            {placeInfo.category}
          </BodyText>
        )}
        {showPriceLevel && (
          <>
            {(placeInfo.rating || placeInfo.category) && (
              <Divider kind="primary" direction="vertical" />
            )}
            <GooglePlacePriceLevel priceLevel={placeInfo.priceLevel} size="small" />
          </>
        )}
      </Group>
    ) : undefined

  const { leadingHero, inlineHero } = useMemo(
    () => splitDrawerDetailHero(showMobileFullBleedGallery, gallerySection),
    [gallerySection, showMobileFullBleedGallery],
  )

  return (
    <TripDrawer
      isOpen={isOpen}
      onClose={onClose}
      onAfterClose={onAfterClose}
      onBack={onBack}
      animation={animation}
      direction="right-to-left"
      presentation={drawerPresentation}
      onDelete={showDeleteOption ? handleDelete : undefined}
      header={
        !showTitleInContent ? (
          <TripDrawerHeaderTitle title={placeName} subtitle={headerSubtitle} />
        ) : undefined
      }
      headerActions={addToTripAction}
      footer={
        <TripDrawerAssistantFooter
          presentation={drawerPresentation}
          contextLabel={placeName}
          isOpen={isOpen}
        />
      }
    >
      <TripDrawerAssistantBridge
        presentation={drawerPresentation}
        isOpen={isOpen}
        contextLabel={placeName}
      />
      <DrawerDetailBodyShell leadingHero={leadingHero}>
        <ContentWrapper direction="vertical" gap={40}>
          {showTitleInContent && isError && (
            <Heading variant="heading3">
              {placeName}
            </Heading>
          )}
          {isError && (
            <Group
              direction="vertical"
              gap={8}
              horizontalAlign="center"
              verticalAlign="center"
            >
              <BodyText variant="medium" colour="critical">
                {failedToLoadPlaceInfoText}
              </BodyText>
              <TextButton kind="secondary" size="small" onClick={handleRetry}>
                {retryLabel}
              </TextButton>
            </Group>
          )}

          {!showMobileFullBleedGallery && inlineHero}
          {((showTitleInContent && !isError) ||
            isLoading ||
            (showLoadedContent &&
              (placeInfo.address ||
                placeInfo.website ||
                placePhoneNumber ||
                placeInfo.openingHours ||
                (showTitleInContent &&
                  (placeInfo.rating || placeInfo.category || showPriceLevel))))) && (
            <Group direction="vertical" gap={8}>
              {showTitleInContent && !isError && (
                <Heading variant="heading3">
                  {placeName}
                </Heading>
              )}
              {isLoading && <LoadingBox height={20} width={160} borderRadius="S" />}
              {showLoadedContent &&
                showTitleInContent &&
                (placeInfo.rating || placeInfo.category || showPriceLevel) && (
                <Group direction="horizontal" gap={8} verticalAlign="center">
                  {placeInfo.rating && (
                    <GooglePlaceRatingDisplay
                      rating={placeInfo.rating}
                      ratingsCount={placeInfo.userRatingCount}
                      size={ratingSize}
                    />
                  )}
                  {placeInfo.category && placeInfo.rating && (
                    <Divider kind="primary" direction="vertical" />
                  )}
                  {placeInfo.category && (
                    <BodyText variant="medium" colour="light-foreground-alt">
                      {placeInfo.category}
                    </BodyText>
                  )}
                  {showPriceLevel && (
                    <>
                      {(placeInfo.rating || placeInfo.category) && (
                        <Divider kind="primary" direction="vertical" />
                      )}
                      <GooglePlacePriceLevel priceLevel={placeInfo.priceLevel} size="medium" />
                    </>
                  )}
                </Group>
              )}
              {showLoadedContent && placeInfo.address && (
                <BodyTextBlock
                  variant="medium"
                  startIcon={<LineMapMarkerIcon />}
                >
                  {placeInfo.address}
                </BodyTextBlock>
              )}
              {showLoadedContent && placeInfo.website && (
                <Clickable
                  href={placeInfo.website}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <BodyTextBlock
                    variant="medium"
                    startIcon={<LineGlobeIcon />}
                    weight="bold"
                  >
                    {websiteDisplayLabel}
                  </BodyTextBlock>
                </Clickable>
              )}
              {showLoadedContent && placePhoneNumber && (
                <Clickable href={`tel:${placePhoneNumber}`}>
                  <BodyTextBlock
                    variant="medium"
                    startIcon={<LinePhoneIcon />}
                    weight="bold"
                  >
                    {placePhoneNumber}
                  </BodyTextBlock>
                </Clickable>
              )}
              {showLoadedContent && placeInfo.openingHours && (
                <OpeningHours
                  descriptions={
                    placeInfo.openingHours.weekdayDescriptions ?? []
                  }
                />
              )}
            </Group>
          )}

          {/* Description */}
          {isLoading && (
            <Group direction="vertical" gap={8}>
              <LoadingBox
                height={20}
                borderRadius="S"
                style={{ width: '100%' }}
              />
              <LoadingBox
                height={20}
                borderRadius="S"
                style={{ width: '90%' }}
              />
              <LoadingBox height={20} width={200} borderRadius="S" />
            </Group>
          )}

          {showVenueBookingCard && (
            <VenueBookingCard
              href={placeInfo.website}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Group
                direction="horizontal"
                gap={16}
                verticalAlign="center"
                horizontalAlign="space-between"
                fullWidth
              >
                <Group
                  direction="horizontal"
                  gap={16}
                  verticalAlign="center"
                >
                  <LineCalendarIcon colour="highlight-primary-dark" />
                  <Group direction="vertical" gap={4}>
                    <BodyText variant="medium" weight="bold">
                      {venueBookingTitle}
                    </BodyText>
                    <BodyText variant="small" colour="light-foreground-alt">
                      {venueBookingSubtitle}
                    </BodyText>
                  </Group>
                </Group>
                <TextButton
                  kind="secondary"
                  size="medium"
                  shape="round"
                  nonInteractable
                  endIcon={<LineExternalLinkAltIcon />}
                >
                  {venueBookingLinkLabel}
                </TextButton>
              </Group>
            </VenueBookingCard>
          )}
          {/* Highlights (streamed markdown) */}
          {isHighlightsLoading && (
            <Group direction="vertical" gap={8}>
              <LoadingBox
                height={20}
                borderRadius="S"
                style={{ width: '100%' }}
              />
              <LoadingBox
                height={20}
                borderRadius="S"
                style={{ width: '90%' }}
              />
              <LoadingBox
                height={20}
                borderRadius="S"
                style={{ width: '95%' }}
              />
              <LoadingBox
                height={20}
                borderRadius="S"
                style={{ width: '80%' }}
              />
              <LoadingBox
                height={20}
                borderRadius="S"
                style={{ width: '85%' }}
              />
            </Group>
          )}
          {highlightsMarkdown && (
            <MarkdownRender
              content={highlightsMarkdown}
              fontSize="medium"
              type="compact"
            />
          )}
          {isHighlightsStreamError && (
            <Group
              direction="vertical"
              gap={8}
              horizontalAlign="center"
              verticalAlign="center"
            >
              <BodyText variant="medium" colour="critical">
                {failedToLoadHighlightsText}
              </BodyText>
              <TextButton
                kind="secondary"
                size="small"
                onClick={handleRetryHighlights}
              >
                {retryLabel}
              </TextButton>
            </Group>
          )}

          {/* Reviews */}
          {showLoadedContent && (placeInfo.rating || mappedReviews?.length) && (
            <>
              <Divider kind="primary" />
              <ExploreReviews
                rating={placeInfo.rating}
                ratingsCount={placeInfo.userRatingCount}
                reviews={mappedReviews}
              />
            </>
          )}

          {/* Location Map */}
          {isLoading && (
            <DrawerDetailMapFrame>
              <LoadingBox floating borderRadius="L" />
            </DrawerDetailMapFrame>
          )}
          {showLoadedContent && (
            <Group direction="vertical" gap={12}>
              <Heading variant="heading5">{locationHeading}</Heading>
              <DrawerDetailMap
                markers={mapMarkers}
                initialCenterPlaceId={googlePlaceId}
                itemId={itemId}
                onEnlarge={handleEnlargeMap}
              />
            </Group>
          )}
        </ContentWrapper>
      </DrawerDetailBodyShell>
    </TripDrawer>
  )
}

export default PlaceInfoDrawer
